import { t } from "../i18n"
import { CwOfficeAlert, CwPunchKind, CwPunchLocation, CwTimeClockSettings, CwUserLite } from "../Interfaces"

export function punchKindLabel(kind: CwPunchKind): string {
  if (kind === "CLOCK_OUT") return t("timeClock.kindClockOut")
  if (kind === "TASK_CHANGE") return t("timeClock.kindTaskChange")
  return t("timeClock.kindClockIn")
}

export function userName(user: CwUserLite | null | undefined): string {
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim()
  return name || t("timeClock.unknownUser")
}

/** "1.2 mi from job site" or "" when no distance was measured. */
export function punchDistanceLabel(punch: Pick<CwPunchLocation, "distanceMiles" | "referenceKind">): string {
  if (punch.distanceMiles == null || !punch.referenceKind) return ""
  return t("timeClock.distanceFrom", {
    miles: punch.distanceMiles.toFixed(punch.distanceMiles < 10 ? 2 : 1),
    reference: punch.referenceKind === "office" ? t("timeClock.referenceOffice") : t("timeClock.referenceJobSite"),
  })
}

export type AlertState = "open" | "allowed" | "used" | "dismissed"

export function alertState(alert: Pick<CwOfficeAlert, "overrideAt" | "consumedAt" | "dismissedAt">): AlertState {
  if (alert.dismissedAt) return "dismissed"
  if (alert.overrideAt && alert.consumedAt) return "used"
  if (alert.overrideAt) return "allowed"
  return "open"
}

export function alertStatusLabel(alert: CwOfficeAlert, formatAt: (value: string) => string): string {
  const state = alertState(alert)
  if (state === "dismissed") {
    return t("timeClock.statusDismissed", { name: userName(alert.dismissedByUser), at: formatAt(alert.dismissedAt as string) })
  }
  if (state === "allowed" || state === "used") {
    const label = t(state === "used" ? "timeClock.statusUsed" : "timeClock.statusAllowed", {
      name: userName(alert.overrideByUser),
      at: formatAt(alert.overrideAt as string),
    })
    return alert.overrideNote ? `${label} — ${alert.overrideNote}` : label
  }
  return t("timeClock.statusOpen")
}

export interface SettingsForm {
  address: string
  city: string
  state: string
  zipcode: string
  officeLatitude: string
  officeLongitude: string
  farPunchThresholdMiles: string
  adminCustomPayrollCode: string
  adminPayrollJobNumber: string
}

export function settingsToForm(s: CwTimeClockSettings | undefined): SettingsForm {
  const str = (v: unknown) => (v == null ? "" : String(v))
  return {
    address: str(s?.address),
    city: str(s?.city),
    state: str(s?.state),
    zipcode: str(s?.zipcode),
    officeLatitude: str(s?.officeLatitude),
    officeLongitude: str(s?.officeLongitude),
    farPunchThresholdMiles: str(s?.farPunchThresholdMiles ?? 0.5),
    adminCustomPayrollCode: str(s?.adminCustomPayrollCode),
    adminPayrollJobNumber: str(s?.adminPayrollJobNumber),
  }
}

/** Validate the settings form; returns the PATCH body or an error message. */
export function formToSettings(
  form: SettingsForm,
): { body: Partial<Omit<CwTimeClockSettings, "companyId">> } | { error: string } {
  const optionalNumber = (value: string, field: string): number | null | { error: string } => {
    const v = value.trim()
    if (!v) return null
    const n = Number(v)
    return Number.isFinite(n) ? n : { error: t("timeClock.numberRequired", { field }) }
  }
  const lat = optionalNumber(form.officeLatitude, t("timeClock.latitudeLabel"))
  if (lat && typeof lat === "object") return lat
  const lng = optionalNumber(form.officeLongitude, t("timeClock.longitudeLabel"))
  if (lng && typeof lng === "object") return lng
  if ((lat == null) !== (lng == null)) return { error: t("timeClock.coordsPair") }
  const threshold = Number(form.farPunchThresholdMiles.trim() || "0.5")
  if (!Number.isFinite(threshold) || threshold <= 0) {
    return { error: t("timeClock.numberRequired", { field: t("timeClock.thresholdLabel") }) }
  }
  const code = form.adminCustomPayrollCode.trim()
  if (code && !/^\d+(\.\d{1,3})?$/.test(code)) {
    return { error: t("timeClock.numberRequired", { field: t("timeClock.customCodeLabel") }) }
  }
  const job = form.adminPayrollJobNumber.trim()
  if (job && !/^\d+$/.test(job)) {
    return { error: t("timeClock.numberRequired", { field: t("timeClock.adminJobLabel") }) }
  }
  const text = (v: string) => v.trim() || null
  return {
    body: {
      address: text(form.address),
      city: text(form.city),
      state: text(form.state),
      zipcode: text(form.zipcode),
      officeLatitude: lat as number | null,
      officeLongitude: lng as number | null,
      farPunchThresholdMiles: threshold,
      adminCustomPayrollCode: code || null,
      adminPayrollJobNumber: job || null,
    },
  }
}

/** Sage cost code check used by the Admin task form (matches the API). */
export function isSageCostCode(value: string): boolean {
  return /^\d+(\.\d{1,3})?$/.test(value.trim()) && Number(value) > 0
}
